import React, {FC, useMemo, useState} from "react";
import {Box, Card, Typography} from "@material-ui/core";
import format from "tools/formats/formatDate";
import {grey} from "@material-ui/core/colors";
import {apiV2} from "api/firebase";
import {invalidateTaskTimelineQueries} from "api/taskTimelineCache";
import DataRow from "view/components/DataRow";
import {
  getStatus,
  getStatusTextColor,
} from "view/components/TasksList/components/TaskDetailsDrawer/helpers";
import RichText from "view/components/RichText";
import Alert from "view/components/Alert";
import {generateTestId} from "tools/cypress/generateTestId";
import {TaskAttachments} from "view/components/TaskDrawer";
import TaskSubtaskList from "view/components/TaskDrawer/TaskSubtaskList";
import {unpackFileAttachment} from "tools/product/mapAttachmentFieldToTaskAttachment";
import CalendarIcon from "@material-ui/icons/EventNote";
import StatusIcon from "@material-ui/icons/RadioButtonUnchecked";
import PeopleIcon from "@material-ui/icons/People";
import ListAltIcon from "@material-ui/icons/ListAlt";
import DomainIcon from "@material-ui/icons/Domain";
import PinDropIcon from "@material-ui/icons/PinDrop";
import getTextWithVariables from "tools/task/getTextWithVariables";
import {getReadableString, getShowDateFormat} from "constraints/general";
import OrganiserReactionMessageBlock from "view/components/TasksList/components/OrganiserReactionMessageBlock";
import ReviewerAttribution from "view/components/TasksList/components/TaskDetailsDrawer/Components/ReviewerAttribution";
import PrintedTaskAttachment from "view/components/TasksList/components/TaskDetailsDrawer/Components/PrintedTaskAttachment";
import ApprovalActionButtons from "view/components/TasksList/components/ApprovalActionButtons";
import ResponseBlock from "view/components/TasksList/components/ResponseBlock";
import Tags from "view/components/Tags";
import {TaskFileShareProvider} from "view/context/TaskFileShareContext";
import {
  composeMultitaskSubtasks,
  formatReviewerDecisionAt,
  joinReviewersWithReactionFiles,
} from "view/components/TasksList/components/TaskDetailsDrawer/helpers/drawerStateAdapters";
import RevisionModal, {
  SubmitRevisionMessageParams,
} from "view/components/TasksList/components/Modals/RevisionModal";
import {
  IBuildType,
  TaskApprovalType,
  TaskMultipleReviewersMode,
  TaskResponseType,
  TaskStatus,
} from "@evennt-tech/api-v2-core";
import {
  IAnonymousShowProjection,
  IVerifyTaskEmailReviewRes,
} from "@evennt-tech/api-v2-external";
import {IStandTaskListRow} from "ts/api/stand-task-list-row";
import {definitionGridToSchema} from "tools/task/grid/mappers";
import {resolveEffectiveDueDate} from "tools/task/resolveEffectiveDueDate";
import {singleSubmit} from "./ApprovalModal/submit";
import ApprovalModal from "./ApprovalModal";
import useStyles from "./styles";

/**
 * Append the anonymous reviewer's magic-link `key` to a non-public task file
 * URL so the BE redirect (`/task/reviewer-reaction/file/...`) can authorise
 * the read without a Firebase session. No-op for empty URLs.
 */
const withReviewKey = (url: string, key: string): string => {
  if (!url || !key) return url;
  return `${url}${url.includes("?") ? "&" : "?"}key=${encodeURIComponent(key)}`;
};

type Props = {
  taskReview: IVerifyTaskEmailReviewRes<string>;
  onSubmit: (expectPdf: boolean) => void;
  reviewKey: string;
};

const TaskReviewCard: FC<Props> = ({taskReview, onSubmit, reviewKey: key}) => {
  const classes = useStyles();

  const {
    task,
    instance,
    response,
    payload,
    reviewers: rawReviewers,
    reviewerReactionFiles: rawReviewerReactionFiles,
    reviewerOrganisers: rawReviewerOrganisers,
    responseHistory,
    multitaskSubtasks: rawMultitaskSubtasks,
    // A sub-task answer newer than the submission under review: the exhibitor
    // committed a changed sub-task on an already-submitted multitask and never
    // pressed the overall Submit, so nothing came back for review and what this
    // reviewer is about to approve no longer matches the answers.
    //
    // Server-computed (`multitask-resubmit.helper` on the BE) — the same fact,
    // from the same rule, that the in-app organiser drawer shows. The email
    // reviewer holds the approve link, so they need it at least as much.
    needsResubmit,
    changedAfterSubmitSubtaskIds,
    gridSchema,
    printTaskFile,
    show: showProj,
    showOwnerName,
    stand,
    exhibitorPairId,
    company,
    taskTags,
    customFields,
    standToCustomFields,
    reviewerEmail,
  } = taskReview;

  // Join reviewers with their reaction files by reviewerId, and flatten
  // multitask subtasks into the row list `TaskSubtaskList` expects.
  const reviewers = useMemo(
    () =>
      joinReviewersWithReactionFiles(
        rawReviewers ?? [],
        rawReviewerReactionFiles ?? []
      ),
    [rawReviewers, rawReviewerReactionFiles]
  );
  const multitaskSubtasks = useMemo(
    () => composeMultitaskSubtasks(rawMultitaskSubtasks ?? null),
    [rawMultitaskSubtasks]
  );

  // Organiser-reviewer display names, shaped for `ReviewerAttribution` (the
  // anonymous page has no organiser directory — the BE sends the names).
  const showOrganiserList = useMemo(
    () =>
      (rawReviewerOrganisers ?? []).map((organiser) => ({
        value: organiser.id,
        label: organiser.name,
        company: organiser.company,
      })),
    [rawReviewerOrganisers]
  );

  // Row for status helpers and RevisionModal.
  const row = useMemo<IStandTaskListRow>(
    () => ({
      task,
      instance,
      resolvedDueDate: resolveEffectiveDueDate(task, {
        liveStart: showProj.liveStart,
        invitationDate: stand.invitationDate,
      }),
      buildTypes: [] as IBuildType[],
      stand,
      company: company
        ? ({
            id: company.id,
            companyId: company.companyId,
            showId: task.showId,
            company: {
              id: company.companyId,
              name: company.name ?? "",
              suffixName: company.suffixName ?? "",
            },
          } as IStandTaskListRow["company"])
        : undefined,
      // Email-review cards render no per-stand badges.
      tagIds: [],
      hasAttachments: false,
      multitask: null,
      // Whose submission is under review — resolved server-side from the
      // instance's responding pair, so the card never infers a company from
      // the stand (a stand can host several).
      exhibitorPairId,
    }),
    [task, instance, stand, company, exhibitorPairId, showProj.liveStart]
  );

  const show: IAnonymousShowProjection<string> = showProj;

  const [isRevisionOpen, setIsRevisionOpen] = useState(false);
  const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false);
  const [isApproval, setIsApproval] = useState(false);

  const approvalType = task.approvalConfig?.type ?? null;
  const isMultiReviewer = approvalType === TaskApprovalType.MULTIPLE_REVIEWERS;
  const isEmailReview = approvalType === TaskApprovalType.EMAIL;
  const isSequential =
    task.approvalConfig?.multipleReviewersMode ===
    TaskMultipleReviewersMode.SEQUENTIAL;

  // Sequential mode: show approve controls only if this reviewer is the
  // next pending in queue. Parallel and single-reviewer always show them.
  const myIndex = reviewers.findIndex((r) => r.reviewerEmail === reviewerEmail);
  const sequentialReady =
    myIndex === 0
      ? true
      : myIndex > 0
      ? !!reviewers[myIndex - 1]?.isApproved
      : false;
  const approvalVisible = isMultiReviewer
    ? !isSequential || sequentialReady
    : true;
  const isReviewByEmail = isEmailReview || (isMultiReviewer && approvalVisible);

  const myReviewer = reviewers.find((r) => r.reviewerEmail === reviewerEmail);
  const alreadyDecided = !!(
    myReviewer &&
    (myReviewer.isApproved || myReviewer.isRejected)
  );
  const shouldShowApprovalButton =
    !alreadyDecided &&
    isReviewByEmail &&
    (instance.status === TaskStatus.WAITING_FOR_APPROVAL ||
      instance.status === TaskStatus.IN_REVIEW);

  const dateFormat = getShowDateFormat(show);
  // Spelled-month mask ("d MMM yyyy") for the reviewer decision timestamp,
  // matching the drawer — distinct from the numeric `dateFormat` above.
  const readableDateFormat = getReadableString(show);

  // The reviewer who moved the task to revision. Keyed by
  // `movedToRevisionByReviewerId` (email/magic-link reviewer) or
  // `movedToRevisionByOrganiserId` (organiser); falls back to the email
  // reviewer whose reject-shaped row carries the revision message (revisions
  // recorded before the reviewer-id attribution existed). Skipping this
  // reviewer in the per-reviewer loop stops its revision comment rendering as a
  // duplicate mislabeled "Rejection Reason" alongside the revision note.
  const revisionReviewer = instance.revisionMessage
    ? reviewers.find(
        (r) =>
          (!!instance.movedToRevisionByReviewerId &&
            r.id === instance.movedToRevisionByReviewerId) ||
          (!!instance.movedToRevisionByOrganiserId &&
            r.reviewerOrganiserId === instance.movedToRevisionByOrganiserId)
      ) ??
      reviewers.find(
        (r) =>
          !r.reviewerOrganiserId &&
          r.isRejected &&
          !!r.rejectMessage &&
          r.rejectMessage === instance.revisionMessage
      )
    : undefined;

  // Reaction files are `public: false`; the anonymous reviewer has no Firebase
  // session, so authorise the redirect with the magic-link `?key=`.
  const mapReactionFiles = (files: typeof reviewers[number]["reactionFiles"]) =>
    files.map((rf) => {
      const f = rf.file;
      return unpackFileAttachment({
        id: rf.fileId,
        title: f?.title || f?.name || "",
        url: withReviewKey(f?.url || "", key),
        file: {name: f?.name || "", size: f?.size || 0},
      });
    });

  const onApprove = () => {
    setIsApproval(true);
    setIsApprovalModalOpen(true);
  };
  const onReject = () => {
    setIsApproval(false);
    setIsApprovalModalOpen(true);
  };

  // Revision flow — submits the verdict via `singleSubmit`, then
  // invalidates the timeline so the new reaction files refetch.
  const handleTaskRevision = async ({
    files,
    message,
  }: SubmitRevisionMessageParams) => {
    await singleSubmit({
      showId: show.id,
      key: key!,
      // Pin the verdict to THIS task instance — the per-email magic-link key is
      // shared across every task the reviewer is on, so without (taskId,
      // standId) the BE rejects with 400 (or, before that guard, wrote to an
      // arbitrary instance and the viewed task stayed in WAITING).
      taskId: task.id,
      standId: stand.id,
      approval: false,
      message,
      isRevision: true,
      files,
    });

    invalidateTaskTimelineQueries({
      showId: show.id,
      standId: stand.id,
      taskId: task.id,
    });

    setIsRevisionOpen(false);
    // Revision produces no Task PDF — don't poll.
    onSubmit(false);
  };

  return (
    // Everything this page renders was published by `email-review/verify`, so
    // the attachment chips can offer a plain "copy link" — no share call (the
    // reviewer has no session for it) and no `?key=` in what gets copied.
    <TaskFileShareProvider mode="public">
      <Card className={classes.card} variant="outlined">
        <Typography className={classes.title}>{task.title}</Typography>
        {!!taskTags?.length && (
          <Tags tags={taskTags.map((t) => t.id)} tagDefinitions={taskTags} />
        )}
        <RichText
          content={getTextWithVariables({
            task: {stand, standToCustomFields},
            show,
            customFields,
            description: task.description as string,
          })}
        />
        <Box display="flex" flexDirection="column">
          <DataRow
            label="Status"
            value={getStatus(row)}
            textColor={getStatusTextColor(row)}
            icon={<StatusIcon style={{color: grey[500]}} />}
            actionPanel={<div />}
          />
          <DataRow
            label="Due Date"
            value={
              row.resolvedDueDate
                ? format(row.resolvedDueDate, dateFormat)
                : "XnX"
            }
            icon={<CalendarIcon style={{color: grey[500]}} />}
            actionPanel={<div />}
          />
          <DataRow
            label="Organiser"
            value={showOwnerName}
            icon={<PeopleIcon style={{color: grey[500]}} />}
            actionPanel={<div />}
          />
          <DataRow
            label="Show"
            value={show.name}
            icon={<ListAltIcon style={{color: grey[500]}} />}
            actionPanel={<div />}
          />
          <DataRow
            label="Exhibitor"
            value={company?.name ?? ""}
            icon={<DomainIcon style={{color: grey[500]}} />}
            actionPanel={<div />}
          />
          <DataRow
            label="Stand Number"
            value={stand.name ?? ""}
            icon={<PinDropIcon style={{color: grey[500]}} />}
            actionPanel={<div />}
            last
          />
        </Box>
        {task.responseType === TaskResponseType.MULTITASK ? (
          <TaskSubtaskList
            subtasks={multitaskSubtasks ?? []}
            show={show}
            parentRow={row}
            parentStatus={instance.status}
            customFields={customFields}
            // Per-stand custom-field VALUES (from the BE verify response) so
            // `<CustomField>` tokens in subtask descriptions resolve — without
            // this the subtask list rendered the fallback text.
            standToCustomFields={standToCustomFields}
            // Per-subtask option definitions (full nested tree) so MC /
            // CHECKBOXES subtasks — and their sub-select picks — render
            // human-readable labels instead of raw option uuids. Same source
            // the response drawer uses; the BE verify response already carries
            // it on `multitaskSubtasks.optionsBySubtaskId`.
            subtaskOptionsById={rawMultitaskSubtasks?.optionsBySubtaskId}
            // Per-subtask counterpart of `needsResubmit` — marks the rows whose
            // own answer moved on after the parent submit, so the reviewer sees
            // WHICH answer changed and not just that one did.
            changedAfterSubmitSubtaskIds={changedAfterSubmitSubtaskIds ?? []}
            readonly
            inlineGrid
            onClickSubtask={() => undefined}
          />
        ) : (
          <ResponseBlock
            apiV2={
              task.responseType
                ? {
                    responseType: task.responseType,
                    payload,
                    hasHistory: responseHistory.length > 0,
                    gridSchema: definitionGridToSchema(gridSchema),
                    options: task.options ?? [],
                  }
                : undefined
            }
          />
        )}
        {needsResubmit && (
          // Same wording and severity as the organiser drawer's warning, but
          // without its "Find more details in history timeline" link: the
          // timeline modal reads the authenticated task-timeline endpoints, and
          // this page is anonymous (magic-link key only) — the link would open
          // an empty or failing modal. The per-sub-task "Updated" markers above
          // already point at what changed.
          <Box mt={2} mb={2}>
            <Alert
              severity="warning"
              fullWidth
              {...generateTestId("email-review-subtask-changed-after-submit")}
            >
              The sub-task answer(s) was updated after this task was submitted.
            </Alert>
          </Box>
        )}
        {/* Per-reviewer approval notes — attribution (name/role) + that
            reviewer's own comment + files. Mirrors the organiser drawer's
            multi-approval block so every approver's feedback (not just the
            latest / the viewer's own) is shown with its author and files. */}
        {reviewers.map((reviewer) => {
          // The revision reviewer is rendered once in the revision note below —
          // don't also emit its reject-shaped row here (that's the mislabeled
          // "Rejection Reason" duplicate).
          if (revisionReviewer && reviewer.id === revisionReviewer.id) {
            return null;
          }
          const hasContent =
            !!reviewer.message ||
            !!reviewer.rejectMessage ||
            reviewer.reactionFiles.length > 0;
          if (!hasContent) return null;

          return (
            <Box key={reviewer.id} mb={2}>
              <ReviewerAttribution
                reviewerOrganiserId={reviewer.reviewerOrganiserId}
                reviewerEmail={reviewer.reviewerEmail}
                localizedOrganiser="Organiser"
                showOrganiserList={showOrganiserList}
                timestamp={formatReviewerDecisionAt(
                  reviewer,
                  readableDateFormat
                )}
              />
              <OrganiserReactionMessageBlock
                task={{
                  approvalMessage: reviewer.message ?? undefined,
                  rejectMessage: reviewer.rejectMessage ?? undefined,
                }}
              />
              {reviewer.reactionFiles.length > 0 && (
                <TaskAttachments
                  readonly
                  hideTitle
                  attachments={mapReactionFiles(reviewer.reactionFiles)}
                />
              )}
            </Box>
          );
        })}
        {/* Instance-level revision note — not stored per reviewer, so
            attribute it to the reviewer who moved it to revision (email →
            "By {email}"). */}
        {!!instance.revisionMessage && (
          <Box mb={2}>
            <ReviewerAttribution
              reviewerOrganiserId={
                revisionReviewer?.reviewerOrganiserId ??
                instance.movedToRevisionByOrganiserId ??
                undefined
              }
              reviewerEmail={revisionReviewer?.reviewerEmail}
              localizedOrganiser="Organiser"
              showOrganiserList={showOrganiserList}
              timestamp={
                revisionReviewer
                  ? formatReviewerDecisionAt(
                      revisionReviewer,
                      readableDateFormat
                    )
                  : undefined
              }
            />
            <OrganiserReactionMessageBlock
              task={{revisionMessage: instance.revisionMessage}}
            />
            {!!revisionReviewer?.reactionFiles.length && (
              <TaskAttachments
                readonly
                hideTitle
                attachments={mapReactionFiles(revisionReviewer.reactionFiles)}
              />
            )}
          </Box>
        )}
        {/* The Task PDF is rendered by a backend process after approval; the
            page silently polls for it and it appears here once ready. */}
        {!!printTaskFile && (
          <PrintedTaskAttachment
            attachment={{
              id: printTaskFile.id,
              url: printTaskFile.url,
              title: printTaskFile.title || printTaskFile.name,
              provider: printTaskFile.provider,
              file: {name: printTaskFile.name, size: printTaskFile.size},
            }}
          />
        )}
        {shouldShowApprovalButton && (
          <Box mt={3}>
            <ApprovalActionButtons
              onRevision={() => setIsRevisionOpen(true)}
              onApprove={onApprove}
              onReject={onReject}
            />
          </Box>
        )}
      </Card>
      {isRevisionOpen && (
        <RevisionModal
          onClose={() => setIsRevisionOpen(false)}
          onSubmit={handleTaskRevision}
          task={row}
        />
      )}
      {isApprovalModalOpen && (
        <ApprovalModal
          dateFormat={dateFormat}
          onClose={() => setIsApprovalModalOpen(false)}
          approval={isApproval}
          taskReview={taskReview}
          customFields={customFields}
          show={show}
          email={reviewerEmail}
          onSubmit={onSubmit}
        />
      )}
    </TaskFileShareProvider>
  );
};

export default TaskReviewCard;
